import { effect, Injectable, signal } from '@angular/core';

/**
 * @description Servicio para gestionar el tema de la aplicación usando signals
 * para una mejor reactividad y sincronización automática con el DOM.
 *
 * Es la única autoridad sobre la clase `dark` del `<html>`: ningún otro servicio
 * debe manipularla.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /**
   * @description Clave de `localStorage` donde se guarda la preferencia del usuario.
   * Se cambió respecto a la anterior (`theme`) para descartar de una vez las
   * preferencias que se habían escrito solas a partir del tema del sistema
   * operativo: hoy el modo oscuro solo se activa si el usuario lo pide.
   */
  private static readonly STORAGE_KEY = 'ieeg-theme';

  /**
   * @description Clave anterior, que se limpia al sincronizar por primera vez
   */
  private static readonly LEGACY_STORAGE_KEY = 'theme';

  /**
   * @description Signal que controla si el modo oscuro está activo.
   * Se inicializa con la preferencia guardada porque el effect del constructor
   * se ejecuta antes que `initializeTheme()` y, con un valor inicial fijo,
   * sobrescribía el tema del usuario con `light`.
   */
  private readonly _isDarkMode = signal<boolean>(ThemeService.resolverPreferenciaInicial());

  /**
   * @description Signal de solo lectura para acceder al estado del tema
   */
  public readonly isDarkMode = this._isDarkMode.asReadonly();

  constructor() {
    // Effect que sincroniza automáticamente el estado del tema con el DOM
    effect(() => {
      const darkMode = this._isDarkMode();
      if (darkMode) {
        document.documentElement.classList.add('dark');
        localStorage.setItem(ThemeService.STORAGE_KEY, 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem(ThemeService.STORAGE_KEY, 'light');
      }

      localStorage.removeItem(ThemeService.LEGACY_STORAGE_KEY);
    });
  }

  /**
   * @description Inicializa el tema basado en la preferencia guardada
   */
  initializeTheme(): void {
    this._isDarkMode.set(ThemeService.resolverPreferenciaInicial());
  }

  /**
   * @description Resuelve el tema inicial a partir de la preferencia guardada.
   * La preferencia del sistema operativo se ignora deliberadamente: sin una
   * elección explícita del usuario, la aplicación siempre arranca en modo claro.
   * @returns {boolean} true si debe usarse el modo oscuro
   */
  private static resolverPreferenciaInicial(): boolean {
    return localStorage.getItem(ThemeService.STORAGE_KEY) === 'dark';
  }

  /**
   * @description Alterna entre el modo oscuro y claro
   * El effect se encarga automáticamente de actualizar el DOM y localStorage
   */
  toggleDarkMode(): void {
    this._isDarkMode.update((current) => !current);
  }

  /**
   * @description Establece el modo de tema específico
   * @param {boolean} isDark - true para modo oscuro, false para modo claro
   */
  setDarkMode(isDark: boolean): void {
    this._isDarkMode.set(isDark);
  }
}
